import { getToken } from "./tokenService";
import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
    clients?: string[];
  };
  message?: string;
}

export interface Plan {
  id: string;
  name: string;
  monthly_call_limit: number | string;
  monthly_token_limit: number | string;
  excess_rate_per_1m_tokens: number | string;
  alert_threshold_1_percent: number | string | null;
  alert_threshold_2_percent: number | string | null;
  created_at: string;
  updated_at: string;
}

export interface PlansListResponse {
  data: Plan[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface CreatePlanRequest {
  name: string;
  monthly_call_limit: number;
  monthly_token_limit: number;
  excess_rate_per_1m_tokens: number;
  alert_threshold_1_percent: number | null;
  alert_threshold_2_percent: number | null;
}

export type UpdatePlanRequest = Partial<CreatePlanRequest>;

async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: unknown
): Promise<ApiResponse<T>> {
  const token = getToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || {
        code: "REQUEST_FAILED",
        message: data.message || "Erro na requisição",
      },
    };
  }

  return data;
}

export async function listPlans(params?: {
  limit?: number;
  offset?: number;
  search?: string;
}) {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  if (params?.search) query.set("search", params.search);

  const suffix = query.toString() ? `?${query.toString()}` : "";
  return apiRequest<{ data: Plan[]; pagination: PlansListResponse["pagination"] }>(
    `/plans${suffix}`
  );
}

export async function getPlanById(id: string) {
  return apiRequest<Plan>(`/plans/${id}`);
}

export async function createPlan(payload: CreatePlanRequest) {
  return apiRequest<Plan>("/plans", "POST", payload);
}

export async function updatePlan(id: string, payload: UpdatePlanRequest) {
  return apiRequest<Plan>(`/plans/${id}`, "PUT", payload);
}

export async function deletePlan(id: string) {
  return apiRequest<void>(`/plans/${id}`, "DELETE");
}

export interface PlanUsageClient {
  id: string;
  identifier: string | null;
  name: string | null;
  company: string | null;
}

export interface PlanUsagePlanInfo {
  plan_id: string | null;
  name: string | null;
  source: "catalog" | "enterprise" | string;
  call_limit: number;
  token_limit: number;
  excess_rate_per_1m_tokens: number;
}

export interface PlanUsageItem {
  period: { month: number; year: number };
  client_id: string;
  client: PlanUsageClient;
  plan: PlanUsagePlanInfo | null;
  in_plan_calls: number;
  excess_calls: number;
  in_plan_tokens: number;
  excess_tokens: number;
  excess_cost: number;
}

export interface PlanUsageListResponse {
  period: { month: number; year: number };
  clients: PlanUsageItem[];
}

export async function getPlanUsage(options: {
  month: number | string;
  year: number | string;
  client_id?: string;
}) {
  const params = new URLSearchParams();
  params.set("month", String(options.month));
  params.set("year", String(options.year));
  if (options.client_id) {
    params.set("client_id", options.client_id);
  }

  return apiRequest<PlanUsageItem | PlanUsageListResponse>(
    `/plans/usage?${params.toString()}`,
  );
}
